import {
    Box,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import type { AuditRecordDetail } from "../../models/Audit";

import {
    getAuditActionLabel,
    getAuditEntityLabel,
    parseAuditJson
} from "../../functions/auditHelpers";

interface Props {
    open: boolean;
    record: AuditRecordDetail | null;
    loading?: boolean;
    onClose: () => void;
}

export default function AuditDetailDrawer({
    open,
    record,
    loading = false,
    onClose
}: Props) {

    const before =
        parseAuditJson<Record<string, unknown>>(
            record?.beforeJson
        );

    const after =
        parseAuditJson<Record<string, unknown>>(
            record?.afterJson
        );

    const metadata =
        parseAuditJson<Record<string, unknown>>(
            record?.metadataJson
        );

    const changedFields: string[] =
        Array.isArray(metadata?.changedFields)
            ? (metadata?.changedFields as string[])
            : getChangedFields(before, after);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: {
                        xs: "100%",
                        sm: 650
                    },
                    p: 3
                }
            }}
        >
            {loading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    mt={5}
                >
                    <CircularProgress />
                </Box>
            ) : record ? (
                <>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Box>
                            <Typography variant="h5">
                                {getAuditActionLabel(record.action)}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {new Date(
                                    record.createdUtc
                                ).toLocaleString("en-GB")}
                            </Typography>
                        </Box>

                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <Stack spacing={1}>
                        <DetailRow
                            label="User"
                            value={
                                record.userDisplayName ||
                                record.userEmail ||
                                "System"
                            }
                        />

                        {record.userEmail && (
                            <DetailRow
                                label="Email"
                                value={record.userEmail}
                            />
                        )}

                        <DetailRow
                            label="Entity"
                            value={getAuditEntityLabel(
                                record.entityType,
                                record.entityId,
                                record.rootEntityType,
                                record.rootEntityId
                            )}
                        />

                        <DetailRow
                            label="Audit ID"
                            value={record.id.toString()}
                        />
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <Typography
                        variant="h6"
                        gutterBottom
                    >
                        Changes
                    </Typography>

                    {changedFields.length > 0 ? (
                        <AuditChangeTable
                            fields={changedFields}
                            before={before}
                            after={after}
                        />
                    ) : (
                        <Typography color="text.secondary">
                            No field-level change information
                            is available for this record.
                        </Typography>
                    )}

                    {record.correlationId && (
                        <>
                            <Divider sx={{ my: 3 }} />

                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Correlation ID:{" "}
                                {record.correlationId}
                            </Typography>
                        </>
                    )}
                </>
            ) : null}
        </Drawer>
    );
}

function DetailRow({
    label,
    value
}: {
    label: string;
    value: string;
}) {
    return (
        <Stack
            direction="row"
            spacing={2}
        >
            <Typography
                sx={{
                    width: 110,
                    flexShrink: 0
                }}
                color="text.secondary"
            >
                {label}
            </Typography>

            <Typography>
                {value}
            </Typography>
        </Stack>
    );
}

function AuditChangeTable({
    fields,
    before,
    after
}: {
    fields: string[];
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
}) {
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>
                            Field
                        </TableCell>

                        <TableCell>
                            Before
                        </TableCell>

                        <TableCell>
                            After
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {fields.map(field => (
                        <TableRow key={field}>
                            <TableCell>
                                {formatFieldName(field)}
                            </TableCell>

                            <TableCell>
                                {formatValue(
                                    getPropertyValue(
                                        before,
                                        field
                                    )
                                )}
                            </TableCell>

                            <TableCell>
                                {formatValue(
                                    getPropertyValue(
                                        after,
                                        field
                                    )
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function getPropertyValue(
    obj: Record<string, unknown> | null,
    field: string
): unknown {
    if (!obj)
        return null;

    /*
     * System.Text.Json may be camelCase while
     * metadata changedFields contains C# property names.
     */
    if (field in obj)
        return obj[field];

    const camel =
        field.charAt(0).toLowerCase() +
        field.slice(1);

    return obj[camel];
}

function formatValue(value: unknown): string {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "—";
    }

    if (typeof value === "object")
        return JSON.stringify(value);

    return String(value);
}

function formatFieldName(field: string): string {
    return field
        .replace(/([A-Z])/g, " $1")
        .trim();
}

function getChangedFields(
    before: Record<string, unknown> | null,
    after: Record<string, unknown> | null
): string[] {
    const keys =
        new Set([
            ...Object.keys(before ?? {}),
            ...Object.keys(after ?? {})
        ]);

    return Array.from(keys)
        .filter(key =>
            JSON.stringify(before?.[key]) !==
            JSON.stringify(after?.[key])
        );
}